/**
 * Field Parser Utility for Challenge Form Analytics
 * Simplified to work with new Form format
 */

import type { ProgramItemDto } from '@/api/types/dtos/analytics/AnalyticsDto';
import type { Question } from '@/api/types/forms';

/**
 * Build simplified field key for backend storage
 * Format: questionId
 * With FOR_EACH: questionId?forEach=["value1","value2"]
 */
export function buildFieldKey(
  questionId: string,
  forEachValues?: string[]
): string {
  if (forEachValues && forEachValues.length > 0) {
    const encoded = JSON.stringify(forEachValues);
    return `${questionId}?forEach=${encodeURIComponent(encoded)}`;
  }
  return questionId;
}

/**
 * Build human-readable field label
 * Format: "Program / Version / Item / Field"
 */
export function buildFieldLabel(
  programName: string,
  versionName: string,
  itemName: string,
  fieldName: string
): string {
  return `${programName} / ${versionName} / ${itemName} / ${fieldName}`;
}

/**
 * Parse simplified field key back to components
 */
export function parseFieldKey(fieldKey: string): {
  questionId: string;
  forEachValues?: string[];
} | null {
  // Extract forEach query parameter if present
  let forEachValues: string[] | undefined;
  let questionId = fieldKey;

  const queryIndex = fieldKey.indexOf('?');
  if (queryIndex !== -1) {
    const queryString = fieldKey.substring(queryIndex + 1);
    questionId = fieldKey.substring(0, queryIndex);

    const params = new URLSearchParams(queryString);
    const forEachParam = params.get('forEach');
    if (forEachParam) {
      try {
        forEachValues = JSON.parse(decodeURIComponent(forEachParam)) as string[];
      } catch (e) {
        console.warn('Failed to parse forEach parameter:', e);
      }
    }
  }

  if (!questionId) return null;

  return {
    questionId,
    forEachValues,
  };
}

/**
 * Get field label from item and question
 */
export function getFieldLabel(
  item: ProgramItemDto,
  question: Question,
  programName: string,
  versionName: string
): string {
  const itemName = item.cpNumber ? `${item.cpNumber}: ${item.name}` : item.name;
  const fieldName = question.text || question.id;
  return buildFieldLabel(programName, versionName, itemName, fieldName);
}
